import { useEffect, useRef, type ComponentType, type KeyboardEvent } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@nexus/ui";

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export interface UnsavedChangeRow {
  id: string;
  title: string;
  detail: string;
  Icon?: ComponentType<{ className?: string }>;
  onReview?: () => void;
}

export interface OrgDrawerUnsavedChangesGuardProps {
  open: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
  onSaveAndClose?: () => void;
  isSaveAndCloseDisabled?: boolean;
  changes?: UnsavedChangeRow[];
  /** Override the scope's `title` / `body` / `discard` copy, e.g. with change counts. */
  title?: string;
  body?: string;
  discardLabel?: string;
  /** i18n prefix; keys `title`, `body`, `keepEditing`, `discard` under it. */
  translationScope?: string;
}

/**
 * In-drawer scrim when closing Identity Hub org overlays with unsaved work.
 * Layout matches the Wonder "Unsaved Changes" dialog: warning header, optional
 * change list, and a muted footer.
 */
export function OrgDrawerUnsavedChangesGuard({
  open,
  onKeepEditing,
  onDiscard,
  onSaveAndClose,
  isSaveAndCloseDisabled = false,
  changes = [],
  title,
  body,
  discardLabel,
  translationScope = "iam.orgDrawer.unsavedGuard",
}: OrgDrawerUnsavedChangesGuardProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);

  // The scrim sits inside the drawer's own focus scope, so it traps focus locally rather
  // than portalling a page-level dialog over the drawer it belongs to.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    keepEditingRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onKeepEditing();
      return;
    }
    if (event.key !== "Tab") return;
    const focusables = [...(containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [])];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-50 flex items-center justify-center bg-background/75 p-4 backdrop-blur-[2px]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="org-unsaved-guard-title"
      aria-describedby="org-unsaved-guard-body"
      onKeyDown={handleKeyDown}
    >
      <div className="w-full max-w-[460px] overflow-hidden rounded-lg bg-background shadow-[0px_10px_30px_-5px_rgba(0,0,0,0.25)]">
        <div className="flex items-start gap-3 px-5 pb-4 pt-5">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-warning-border bg-warning-subtle">
            <AlertTriangle className="size-4 text-warning-subtle-foreground" strokeWidth={2} />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <p id="org-unsaved-guard-title" className="text-[15px] font-semibold leading-snug text-foreground">
              {title ?? t(`${translationScope}.title`, "Discard unsaved changes?")}
            </p>
            <p id="org-unsaved-guard-body" className="text-xs leading-normal text-muted-foreground">
              {body ??
                t(
                  `${translationScope}.body`,
                  "You edited this organization's settings. Closing now will lose those changes.",
                )}
            </p>
          </div>
        </div>

        {changes.length > 0 ? (
          <ul className="mx-5 mb-4 overflow-hidden rounded-md border border-border">
            {changes.map((change, index) => (
              <li
                key={change.id}
                className={
                  index < changes.length - 1
                    ? "flex items-center gap-2.5 border-b border-border-subtle px-3 py-2.5"
                    : "flex items-center gap-2.5 px-3 py-2.5"
                }
              >
                {change.Icon ? <change.Icon className="size-3.5 shrink-0 text-muted-foreground" /> : null}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground">{change.title}</p>
                  <p className="text-[11px] leading-[1.45] text-muted-foreground">{change.detail}</p>
                </div>
                {change.onReview ? (
                  <button
                    type="button"
                    onClick={change.onReview}
                    className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium leading-none text-primary hover:underline"
                  >
                    {t(`${translationScope}.review`, "Review")}
                    <ArrowRight className="size-3" aria-hidden />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex items-center gap-2 border-t border-border bg-muted px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-transparent hover:text-destructive"
            onClick={onDiscard}
          >
            {discardLabel ?? t(`${translationScope}.discard`, "Discard changes")}
          </Button>
          <span className="flex-1" />
          <Button ref={keepEditingRef} variant="outline" size="sm" onClick={onKeepEditing}>
            {t(`${translationScope}.keepEditing`, "Keep editing")}
          </Button>
          {onSaveAndClose ? (
            <Button size="sm" iconStart="check" disabled={isSaveAndCloseDisabled} onClick={onSaveAndClose}>
              {t(`${translationScope}.saveAndClose`, "Save and close")}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
